import React, { useState } from 'react';
import styles from './StoreOrdering.module.css';
import { UNIT_MEASURE_OPTIONS } from '../../constants/unitMeasures';

/**
 * CurrencyInput - Auto-formatting currency input
 * Type "1234" → displays "$12.34"
 */
const CurrencyInput = ({ value, onChange, placeholder = '$0.00' }) => {
  // Format cents to display string (e.g., 1234 → "$12.34")
  const formatDisplay = (cents) => {
    if (!cents && cents !== 0) return '';
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  // Handle keypress - only allow digits
  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].includes(e.keyCode)) return;
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) return;
    // Block non-numeric
    if (e.key < '0' || e.key > '9') {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    // Strip all non-digits
    const digits = e.target.value.replace(/\D/g, '');
    // Convert to cents (integer)
    const cents = parseInt(digits, 10) || 0;
    // Pass cents value to parent
    onChange(cents);
  };

  const displayValue = formatDisplay(value);

  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={styles['currency-input']}
    />
  );
};

/**
 * NewProductModal - Quick product creation form
 * Allows employees to add new products on the fly
 */
const NewProductModal = ({ onClose, onProductCreated }) => {
  const [name, setName] = useState('');
  const [orderUnit, setOrderUnit] = useState('box');
  const [pricingUnit, setPricingUnit] = useState('lb');
  const [priceCents, setPriceCents] = useState(0); // Store price in cents
  const [estimatedConversion, setEstimatedConversion] = useState('');
  const [unitMeasure, setUnitMeasure] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const orderUnits = ['box', 'case', 'bag', 'each', 'pack', 'bundle'];
  const pricingUnits = ['lb', 'kg', 'each', 'oz', 'unit'];

  const hasDualUnits = orderUnit !== pricingUnit;
  const isValid = name.trim() && priceCents > 0;

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const productData = {
        name: name.trim(),
        orderUnit,
        pricingUnit,
        pricePerPricingUnit: priceCents / 100, // Convert cents to dollars
        estimatedConversion: hasDualUnits && estimatedConversion
          ? parseFloat(estimatedConversion)
          : null,
        unitMeasure: unitMeasure || null,
      };

      await onProductCreated(productData);
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Error al crear producto. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles['new-product-modal-overlay']} onClick={onClose}>
      <div className={styles['new-product-modal']} onClick={e => e.stopPropagation()}>
        <div className={styles['new-product-modal-header']}>
          <h3>Nuevo Producto</h3>
          <button className={styles['modal-close-btn']} onClick={onClose}>&times;</button>
        </div>

        <div className={styles['new-product-modal-body']}>
          <div className={styles['form-field']}>
            <label>Nombre del Producto</label>
            <input
              type="text"
              placeholder="e.g., Aguayon"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className={styles['form-row']}>
            <div className={styles['form-field']}>
              <label>Viene en</label>
              <select value={orderUnit} onChange={e => setOrderUnit(e.target.value)}>
                {orderUnits.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>

            <div className={styles['form-field']}>
              <label>Precio por</label>
              <select value={pricingUnit} onChange={e => setPricingUnit(e.target.value)}>
                {pricingUnits.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles['form-field']}>
            <label>Precio por {pricingUnit}</label>
            <CurrencyInput
              value={priceCents}
              onChange={setPriceCents}
              placeholder="$0.00"
            />
          </div>

          <div className={styles['form-field']}>
            <label>Medida (opcional)</label>
            <select value={unitMeasure} onChange={e => setUnitMeasure(e.target.value)}>
              {UNIT_MEASURE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {hasDualUnits && (
            <div className={styles['form-field']}>
              <label>Conversion estimada (opcional)</label>
              <div className={styles['unit-input-wrapper']}>
                <input
                  type="number"
                  placeholder="ej. 40"
                  value={estimatedConversion}
                  onChange={e => setEstimatedConversion(e.target.value)}
                  min="0"
                  step="0.1"
                />
                <span className={styles['unit-suffix']}>{pricingUnit} por {orderUnit}</span>
              </div>
            </div>
          )}
        </div>

        <div className={styles['new-product-modal-footer']}>
          <button
            className={styles['create-product-btn']}
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? 'Creando...' : 'Crear Producto'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewProductModal;
